import { message } from 'antd'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components/macro'
import {
  HARBOUR_HANDOFF_COLUMNS,
  HARBOUR_HANDOFF_START_WORK_FIRST,
  HARBOUR_OUTCOME,
  HARBOUR_PARK_REASON,
  HARBOUR_PARK_REASONS,
  HARBOUR_SNOOZE_DAYS,
  getHarbourColumnLabel,
  getHarbourParkReasonLabel,
} from 'src/constants/harbour'
import {
  handoffHarbourCase,
  parkHarbourCase,
  snoozeHarbourCase,
  undoHarbourOfframp,
} from 'src/utils/harbourApi'
import {
  buildHarbourHandoffBody,
  buildHarbourParkBody,
  buildHarbourSnoozeBody,
  buildHarbourUndoOfframpBody,
  canHandoffHarbour,
  canParkHarbour,
  canSaveHarbourHandoff,
  canSaveHarbourPark,
  canSnoozeHarbour,
  getHarbourHandoffSendLabel,
  getHarbourInitialParkReason,
  getHarbourParkCaption,
  getHarbourParkSaveLabel,
  getHarbourParkSuccessMessage,
  getHarbourSisterGroupSiteCount,
  getHarbourSnoozeCaption,
  getHarbourSnoozeSuccessMessage,
  shouldShowHarbourParkRecoveredHint,
} from 'src/utils/harbourOfframps'
import {
  toastHarbourMutationError,
  toastHarbourOfframpWithUndo,
} from 'src/utils/harbourToast'
import { HARBOUR_TOOLTIPS } from 'src/utils/harbourTooltips'

/**
 * Drawer off-ramps. Snooze is one click. Park and handoff stay blocked until
 * the required reason / note is filled — KPIs stay honest.
 */
const HarbourDrawerOfframps = ({
  venueId,
  venueName,
  column,
  alreadyOwned = false,
  alreadyTaken = false,
  outcome = '',
  lastOutcome = '',
  sisterGroup = null,
  onWorkChanged,
  onDone,
} = {}) => {
  const [parkReason, setParkReason] = useState('')
  const [parkNote, setParkNote] = useState('')
  const [handoffColumn, setHandoffColumn] = useState('')
  const [handoffNote, setHandoffNote] = useState('')
  const [busy, setBusy] = useState(null)

  useEffect(() => {
    setParkReason('')
    setParkNote('')
    setHandoffColumn('')
    setHandoffNote('')
    setBusy(null)
  }, [venueId])

  useEffect(() => {
    const next = getHarbourInitialParkReason({ lastOutcome })
    if (next) {
      setParkReason(reason => reason || next)
    }
  }, [lastOutcome])

  useEffect(() => {
    if (outcome === HARBOUR_OUTCOME.CLOSED) {
      setParkReason(HARBOUR_PARK_REASON.RECOVERED)
    }
  }, [outcome])

  const siteCount = getHarbourSisterGroupSiteCount(sisterGroup)
  const snoozeOk = canSnoozeHarbour({ alreadyTaken, column })
  const parkOk = canParkHarbour({ alreadyTaken, column })
  const handoffOk = canHandoffHarbour({ alreadyOwned, alreadyTaken, column })
  const canPark = parkOk && canSaveHarbourPark({ parkReason }) && !busy
  const canHandoff =
    handoffOk &&
    canSaveHarbourHandoff({ column: handoffColumn, handoffNote }) &&
    !busy

  const finishOfframp = () => {
    onWorkChanged?.()
    onDone?.()
  }

  const offerUndo = (kind, result, text) => {
    toastHarbourOfframpWithUndo({
      text,
      onUndo: async () => {
        try {
          await undoHarbourOfframp(
            buildHarbourUndoOfframpBody({
              venueId,
              kind,
              venueIds: result?.venueIds,
            }),
          )
          message.success('Undone')
          onWorkChanged?.()
        } catch (err) {
          toastHarbourMutationError(err, { venueName })
        }
      },
    })
  }

  const handleSnooze = days => {
    if (!venueId || !snoozeOk || busy) {
      return
    }

    async function snooze() {
      setBusy(`snooze-${days}`)
      try {
        const result = await snoozeHarbourCase(
          buildHarbourSnoozeBody({ venueId, days, sisterGroup }),
        )
        const applied =
          Array.isArray(result?.venueIds) && result.venueIds.length
            ? result.venueIds.length
            : siteCount
        offerUndo(
          'snooze',
          result,
          getHarbourSnoozeSuccessMessage({ days, siteCount: applied }),
        )
        finishOfframp()
      } catch (err) {
        toastHarbourMutationError(err, { venueName })
      } finally {
        setBusy(null)
      }
    }

    snooze()
  }

  const handlePark = () => {
    if (!venueId || !canPark) {
      return
    }

    async function park() {
      setBusy('park')
      try {
        const result = await parkHarbourCase(
          buildHarbourParkBody({
            venueId,
            parkReason,
            parkNote,
            sisterGroup,
          }),
        )
        const applied =
          Array.isArray(result?.venueIds) && result.venueIds.length
            ? result.venueIds.length
            : siteCount
        offerUndo(
          'park',
          result,
          getHarbourParkSuccessMessage(parkReason, { siteCount: applied }),
        )
        finishOfframp()
      } catch (err) {
        toastHarbourMutationError(err, { venueName })
      } finally {
        setBusy(null)
      }
    }

    park()
  }

  const handleHandoff = () => {
    if (!venueId || !canHandoff) {
      return
    }

    async function handoff() {
      setBusy('handoff')
      try {
        await handoffHarbourCase(
          buildHarbourHandoffBody({
            venueId,
            column: handoffColumn,
            handoffNote,
          }),
        )
        message.success(getHarbourColumnLabel(handoffColumn))
        finishOfframp()
      } catch (err) {
        toastHarbourMutationError(err, { venueName })
      } finally {
        setBusy(null)
      }
    }

    handoff()
  }

  if (alreadyTaken) {
    return null
  }

  return (
    <Section data-testid='harbour-offramps'>
      {snoozeOk ? (
        <Block title={HARBOUR_TOOLTIPS.snooze}>
          <BlockLabel>Snooze</BlockLabel>
          <Caption data-testid='harbour-snooze-caption'>
            {getHarbourSnoozeCaption({ siteCount })}
          </Caption>
          <ChoiceRow>
            {HARBOUR_SNOOZE_DAYS.map(days => (
              <Choice
                key={days}
                type='button'
                disabled={Boolean(busy)}
                data-testid={`harbour-snooze-${days}`}
                onClick={() => handleSnooze(days)}
              >
                {busy === `snooze-${days}` ? 'Snoozing…' : `${days} days`}
              </Choice>
            ))}
          </ChoiceRow>
        </Block>
      ) : null}

      {parkOk ? (
        <Block title={HARBOUR_TOOLTIPS.park} data-testid='harbour-park'>
          <BlockLabel>Park</BlockLabel>
          <Caption data-testid='harbour-park-caption'>
            {getHarbourParkCaption({ siteCount })}
          </Caption>
          {shouldShowHarbourParkRecoveredHint({ outcome, lastOutcome }) ? (
            <Caption data-testid='harbour-park-recovered-hint'>
              {getHarbourParkCaption({ recoveredHint: true })}
            </Caption>
          ) : null}
          <ChoiceRow role='radiogroup' data-testid='harbour-park-reason'>
            {HARBOUR_PARK_REASONS.map(reason => (
              <Choice
                key={reason}
                type='button'
                data-testid={`harbour-park-reason-${reason}`}
                data-selected={parkReason === reason ? 'true' : 'false'}
                onClick={() => setParkReason(reason)}
              >
                {getHarbourParkReasonLabel(reason)}
              </Choice>
            ))}
          </ChoiceRow>
          <FieldLabel htmlFor='harbour-park-note'>Note (optional)</FieldLabel>
          <NoteInput
            id='harbour-park-note'
            data-testid='harbour-park-note'
            rows={2}
            value={parkNote}
            onChange={event => setParkNote(event.target.value)}
          />
          <PrimaryButton
            type='button'
            disabled={!canPark}
            data-testid='harbour-park-save'
            onClick={handlePark}
          >
            {getHarbourParkSaveLabel(parkReason, { busy: busy === 'park' })}
          </PrimaryButton>
        </Block>
      ) : null}

      <Block title={HARBOUR_TOOLTIPS.handoff} data-testid='harbour-handoff'>
        <BlockLabel>Ask sales / Ask product</BlockLabel>
        {!handoffOk ? (
          <Caption data-testid='harbour-handoff-blocked'>
            {HARBOUR_HANDOFF_START_WORK_FIRST}
          </Caption>
        ) : (
          <Caption>
            Needs a note. This is the human confirm — not a ticket.
          </Caption>
        )}
        <ChoiceRow role='radiogroup' data-testid='harbour-handoff-column'>
          {HARBOUR_HANDOFF_COLUMNS.map(key => (
            <Choice
              key={key}
              type='button'
              disabled={!handoffOk || Boolean(busy)}
              data-testid={`harbour-handoff-${key}`}
              data-selected={handoffColumn === key ? 'true' : 'false'}
              onClick={() => setHandoffColumn(key)}
            >
              {getHarbourColumnLabel(key)}
            </Choice>
          ))}
        </ChoiceRow>
        <FieldLabel htmlFor='harbour-handoff-note'>Note</FieldLabel>
        <NoteInput
          id='harbour-handoff-note'
          data-testid='harbour-handoff-note'
          rows={2}
          disabled={!handoffOk}
          value={handoffNote}
          onChange={event => setHandoffNote(event.target.value)}
        />
        <PrimaryButton
          type='button'
          disabled={!canHandoff}
          data-testid='harbour-handoff-save'
          onClick={handleHandoff}
        >
          {getHarbourHandoffSendLabel(handoffColumn, {
            busy: busy === 'handoff',
          })}
        </PrimaryButton>
      </Block>
    </Section>
  )
}

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 8px;
  border-top: 1px solid #e6e8eb;
`

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const BlockLabel = styled.h5`
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const Caption = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const ChoiceRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Choice = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 13px;

  &[data-selected='true'] {
    border-color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
    background: #f4f6f0;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
`

const NoteInput = styled.textarea`
  width: 100%;
  max-width: 28rem;
  padding: 6px 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
`

const PrimaryButton = styled.button`
  align-self: flex-start;
  border: 0;
  background: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  color: #fff;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

export default HarbourDrawerOfframps
